import { Button, mediaQueries, palette } from '@centivo/react-components'
import styled, { css } from 'styled-components'

export const Container = styled.div`
  width: 100%;
  max-width: calc(100vw - 5rem);
  background-color: #f7f4e6;

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return {
          borderRadius: '30px',
        }
      default:
        return {}
    }
  }}

  > div {
    border: 0 !important;
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return css`
            width: 100%;
          `
        default:
          return {}
      }
    }}
  }
`

export const Header = styled.div`
  background-color: #f7f4e6;
  border-bottom: 1px solid ${palette.brand.lightPurple} !important;

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          padding-bottom: 8px;
          @media ${theme.mediaQueries.mobile} {
            padding: 0.75rem 1.25rem 0.75rem !important;
            border-bottom: 0 !important;
          }
        `
      default:
        return {}
    }
  }}
`

export const Title = styled.h2`
  color: ${palette.brand.darkGreen} !important;
  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          font-size: 1.625rem !important;
          font-weight: 500 !important;
          @media ${theme.mediaQueries.mobile} {
            text-align: center;
            font-size: 1.25rem !important;
          }
        `
      default:
        return {}
    }
  }}
`

const buttonHoverState = (color: string = palette.brand.darkGreen) => css`
  border-radius: 0;

  &:hover,
  &:focus {
    color: ${color} !important;
    border-bottom: 1px solid ${color} !important;
  }
`

export const GenericButton = styled.button`
  color: ${palette.brand.gray} !important;

  @media ${mediaQueries.mobile} {
    color: ${palette.brand.newPurple} !important;
  }

  ${buttonHoverState()}

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          font-weight: 400 !important;

          @media ${theme.mediaQueries.mobile} {
            font-weight: 500 !important;
          }
        `
      default:
        return {}
    }
  }}
`

export const ResetButtonContainer = styled.div`
  @media ${mediaQueries.desktop} {
    border-right: 1px solid ${palette.brand.lightPurple} !important;
  }

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          @media ${theme.mediaQueries.desktop} {
            height: 36px;
          }
        `
      default:
        return {}
    }
  }}
`

export const ApplyFilterButton = styled(Button)`
  color: ${palette.brand.newPurple};
  max-width: 335px;
  width: auto;
  font-weight: 500 !important;

  @media ${mediaQueries.mobile} {
    height: 50px !important;
    bottom: 30px;
    left: 50% !important;
    transform: translateX(-50%);
    background-color: ${palette.brand.orange} !important;
    border-radius: 10px;
    color: #44190a !important;
  }

  ${buttonHoverState(palette.brand.darkPurple)}
`

export const MaxMiles = styled.span`
  position: relative;
  float: right;
  margin-right: 4px;
  top: 18px;
  font-size: 0.625rem;
  font-weight: 400;
`

const SelectorContainer = styled.div`
  width: 100%;

  label {
    color: ${palette.brand.darkGray};
  }

  h1 {
    font-size: 1rem;
    color: ${palette.neutral['grey-icons']};
    font-weight: 500;
    margin-bottom: 1rem;
  }

  > div {
    width: 100%;
  }
`

export const DistanceSelectorContainer = styled(SelectorContainer)`
  grid-column: 1;
  grid-row: 1;
  color: ${palette.brand.darkGray};
  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          min-width: 416px;
          width: auto;
          margin-right: 6px;
          font-size: 1.125rem;
          @media ${theme.mediaQueries.mobile} {
            min-width: 285px;
          }
        `
      default:
        return {}
    }
  }}

  p {
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            lineHeight: '20px !important',
            marginTop: '0 !important',
          }
        default:
          return {}
      }
    }}
  }

  @media (min-width: 768px) {
    min-width: 416px;
  }
`

export const GenderSelectorContainer = styled(SelectorContainer)`
  display: flex;
  flex-flow: column wrap;
  margin-top: 1rem;

  > label {
    width: 100%;
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            minWidth: '26rem',
            fontSize: '1.125rem',
            color: palette.brand.darkGray,
            marginBottom: '8px',
          }
        default:
          return {}
      }
    }}
  }

  @media ${mediaQueries.mobile} {
    margin-bottom: unset;

    > .gender-container--filter {
      width: 100%;
      display: flex;

      button {
        flex: 1;
      }
    }
  }
`

export const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-right: 4rem;
  padding-top: 2.5rem;
  color: ${palette.brand.darkGray};

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          margin-right: 6px;
          @media ${theme.mediaQueries.mobile} {
            &:nth-child(3) {
              order: 4;
              border-top: 1px solid #ebeff5;
              margin-top: 1rem;
              padding-top: 1rem;
            }
          }
        `
      default:
        return {}
    }
  }}

  > label:not(:first-of-type) {
    margin-left: 1rem;
  }

  p {
    white-space: nowrap;

    @media ${mediaQueries.mobile} {
      white-space: unset;
    }
  }

  @media (max-width: 1150px) {
    grid-column: 1;
    grid-row: unset;
    padding-top: 1rem;
  }

  @media (max-width: 517px) {
    padding-bottom: 1.5rem;
    flex-wrap: wrap;

    label {
      text-overflow: ellipsis;

      &:not(:first-of-type) {
        margin-left: 0;
        margin-top: 0.75rem;
      }
    }
  }
`

export const SpecialtyContainer = styled.div<{
  flow?: 'row' | 'column'
  numItems?: number
  numColumns?: number
  isSafari?: boolean
}>`
  grid-column: 2;
  grid-row: 1 / span 3;

  @media (min-width: 1300px) {
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return css`
            padding-right: 0;
            min-width: 25rem;
          `
        default:
          return css`
            padding-right: 6rem;
          `
      }
    }}
  }

  .checkbox-container {
    display: grid;
    height: 100%;
    max-height: 15rem;
    overflow-y: auto;
    padding-top: 0.5rem;
    padding-right: 2rem;
    grid-template-columns: repeat(
      ${({ numColumns = 2 }) => numColumns},
      minmax(200px, max-content)
    );
    grid-template-rows: repeat(
      ${({ numItems = 0 }) => (numItems > 5 ? 5 : numItems)},
      1.5rem
    );
    grid-auto-flow: ${({ flow = 'column' }) => flow};
    grid-auto-rows: 1fr;
    column-gap: 4rem;
    row-gap: 1rem;
    justify-content: start;

    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return css`
            padding-right: 0;
            column-gap: 3.75rem;
          `
        default:
          return ''
      }
    }}

    @media ${mediaQueries.mobile} {
      padding-right: 0;
      grid-auto-rows: unset;
    }

    > label {
      margin-right: auto;
      height: min-content;
    }
  }

  > label {
    color: ${palette.brand.darkGray};
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            fontWeight: '500',
            lineHeight: '26px',
            marginTop: '2rem !important',
          }
        default:
          return {}
      }
    }}
  }

  > label:not(:first-of-type) {
    margin-top: 1rem;
  }

  p {
    white-space: nowrap;

    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            fontSize: '1rem',
            fontWeight: '400',
            color: palette.brand.darkGray,
          }
        default:
          return {}
      }
    }}

    @media ${mediaQueries.mobile} {
      white-space: unset;
    }
  }

  @media (max-width: 1150px) {
    grid-column: unset;
    grid-row: unset;
    margin-top: 2rem;

    .checkbox-container {
      grid-template-columns: auto;
      grid-template-rows: repeat(auto-fit, minmax(1.5rem, min-content));
      max-height: unset;
      overflow: unset;

      ${({ theme }) => {
        switch (theme.template) {
          case 'template-4':
            return css`
              grid-template-rows: repeat(3, minmax(1.5rem, min-content));
              @media ${theme.mediaQueries.tablet} {
                grid-template-rows: repeat(5, minmax(1.5rem, min-content));
              }
              @media ${({ theme: innerTheme }) => innerTheme.mediaQueries.mobile} {
                grid-template-rows: repeat(
                  auto-fit,
                  minmax(1.5rem, min-content)
                );
              }
            `
          default:
            return {}
        }
      }}
    }
  }

  ${({ theme }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          @media ${({ theme: innerTheme }) => innerTheme.mediaQueries.mobile} {
            margin-top: 2rem !important;
          }
        `
      default:
        return {}
    }
  }}

  @media ${mediaQueries.mobile} {
    margin-top: unset;
    ${({ isSafari }) => (isSafari ? 'padding-bottom: 4rem;' : '')}
  }
`

export const FilterContent = styled.div<{
  isFacility?: boolean
  hasSpecialties?: boolean
}>`
  ${({ hasSpecialties }) =>
    css`
      display: grid;
      grid-template-columns: auto auto;
      padding-bottom: 3rem;
      grid-column-gap: 3rem;

      input[type='checkbox'] {
        height: 100%;
        width: 100%;
      }

      @media (min-width: 1300px) {
        grid-column-gap: 8rem;
      }

      @media (max-width: 1150px) {
        grid-template-columns: 100%;
        grid-auto-flow: row;
        max-height: 60vh;
        overflow: auto;
        overflow-x: hidden;
      }

      @media ${mediaQueries.mobile} {
        max-height: calc(100vh - 120px);
        grid-template-rows: 100px max-content max-content max-content;
        padding-bottom: 60px;
      }

      ${!hasSpecialties &&
      css`
        @media (min-width: 1150px) {
          > ${CheckboxContainer} {
            flex-direction: column;
            grid-row: 1 / span 2;
            grid-column: 2;

            & > label:not(:first-child) {
              margin-left: 0;
              margin-top: 1rem;
            }
          }
        }
      `}
    `}
  ${({ hasSpecialties }) => !hasSpecialties && css``}
`

export const GenderButton = styled(Button).attrs(() => ({
  edgeType: 'sharp',
  buttonType: 'show-more',
}))`
  font-size: 1rem;
  font-weight: 400;
  background-color: ${palette.neutral.white};
  border: 1px ${palette.brand.newPurple} solid;
  color: ${palette.brand.newPurple};
  width: 33%;
  min-width: 140px;
  white-space: nowrap;
  padding: 0.5rem;
  ${({ theme }) => css`
    min-width: 136px;
    border: 1px solid ${palette.brand.newPurple};
    color: ${palette.brand.newPurple};
    @media ${theme.mediaQueries.mobile} {
      font-size: 0.875rem;
    }
  `}}

  &.active,
  &.active:focus,
  &.active:active {
    background-color: ${palette.brand.newPurple};
    border-color: ${palette.brand.newPurple};
    color: ${palette.neutral.white};
    box-sizing: border-box;
    font-weight: 600;
    ${({ theme }) => {
      switch (theme.template) {
        case 'template-4':
          return {
            backgroundColor: palette.brand.newPurple,
            border: `1px solid ${palette.brand.newPurple}`,
            fontWeight: '500',
          }
        default:
          return {}
      }
    }}
  }

  &:first-of-type {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    border-right: 0;
  }
  &:last-of-type {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    border-left: 0;
  }

  @media ${mediaQueries.mobile} {
    min-width: unset;
    width: unset;
  }
`
